import { useState } from 'react';
import { Box, Tab, Tabs, Typography, Paper, Chip } from '@mui/material';
import type { MessageDefinition, ValidationResult } from '@k-ucap/shared';
import { ValidationSeverity } from '@k-ucap/shared';

interface PreviewPanelProps {
  message: MessageDefinition | null;
  payload: Record<string, unknown>;
  validationResult: ValidationResult | null;
  rawHex: string;
  rawBytes: number[];
  sourceStack?: Array<{ source: string; id: string; field?: string; value?: unknown }>;
}

const TreeNode = ({ label, value, depth = 0 }: { label: string; value: unknown; depth?: number }) => {
  const indent = depth * 2;
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" sx={{ pl: indent }}>
        {label}: null
      </Typography>
    );
  }
  if (typeof value !== 'object') {
    return (
      <Typography variant="body2" sx={{ pl: indent }}>
        {label}: {String(value)}
      </Typography>
    );
  }
  if (Array.isArray(value)) {
    return (
      <Box sx={{ pl: indent }}>
        <Typography variant="body2">{label}: [</Typography>
        <Box sx={{ pl: 2 }}>
          {value.map((v, i) => (
            <TreeNode key={i} label={String(i)} value={v} depth={depth + 1} />
          ))}
        </Box>
        <Typography variant="body2">]</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ pl: indent }}>
      <Typography variant="body2">
        {label}: {'{'}
      </Typography>
      <Box sx={{ pl: 2 }}>
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <TreeNode key={k} label={k} value={v} depth={depth + 1} />
        ))}
      </Box>
      <Typography variant="body2">{'}'}</Typography>
    </Box>
  );
};

export const PreviewPanel = ({
  message,
  payload,
  validationResult,
  rawHex,
  rawBytes,
  sourceStack = [],
}: PreviewPanelProps) => {
  const [tab, setTab] = useState(0);

  if (!message) {
    return (
      <Paper sx={{ height: '100%', p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Preview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          메시지를 선택하면 Preview가 표시됩니다.
        </Typography>
      </Paper>
    );
  }

  const jsonPreview = JSON.stringify(
    {
      messageType: message.messageType,
      name: message.name,
      sourceRole: message.sourceRole,
      targetRole: message.targetRole,
      transferMode: message.transferMode,
      dictionaryVersion: message.dictionaryVersion,
      payload,
    },
    null,
    2
  );

  const severityColor = (severity: string) => {
    if (severity === ValidationSeverity.PASS) return 'success';
    if (severity === ValidationSeverity.WARN) return 'warning';
    if (severity === ValidationSeverity.FAIL) return 'error';
    return 'default';
  };

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
          <Tab label="JSON" />
          <Tab label="Tree" />
          <Tab label="HEX" />
          <Tab label="Validation" />
          <Tab label="Source" />
        </Tabs>
      </Box>
      <Box sx={{ p: 2, overflow: 'auto', flexGrow: 1 }}>
        {tab === 0 && (
          <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
            {jsonPreview}
          </Box>
        )}
        {tab === 1 && (
          <Box>
            <TreeNode label="messageType" value={message.messageType} />
            <TreeNode label="name" value={message.name} />
            <TreeNode label="sourceRole" value={message.sourceRole} />
            <TreeNode label="targetRole" value={message.targetRole} />
            <TreeNode label="transferMode" value={message.transferMode} />
            <TreeNode label="payload" value={payload} />
          </Box>
        )}
        {tab === 2 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Message Preview Encoding
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {rawHex || 'HEX를 생성하려면 Send를 실행하세요.'}
            </Typography>
            {rawBytes.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace' }}>
                {rawBytes.length} bytes
              </Typography>
            )}
          </Box>
        )}
        {tab === 3 && (
          <Box>
            {validationResult ? (
              <>
                <Chip
                  label={validationResult.severity}
                  color={severityColor(validationResult.severity) as never}
                  sx={{ mb: 1 }}
                />
                {validationResult.issues.length === 0 ? (
                  <Typography variant="body2">이상 없음</Typography>
                ) : (
                  validationResult.issues.map((issue, idx) => (
                    <Box key={idx} sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        <Chip label={issue.severity} size="small" color={severityColor(issue.severity) as never} />{' '}
                        {issue.code}: {issue.message}
                      </Typography>
                    </Box>
                  ))
                )}
              </>
            ) : (
              <Typography variant="body2">Validation을 실행하지 않았습니다.</Typography>
            )}
          </Box>
        )}
        {tab === 4 && (
          <Box>
            {sourceStack && sourceStack.length > 0 ? (
              sourceStack.map((source, idx) => (
                <Box key={idx} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <Chip label={source.source} size="small" sx={{ mr: 1 }} />
                    {source.id}
                    {source.field !== undefined && ` / ${source.field}`}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2">소스 스택 정보가 없습니다.</Typography>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};
